import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, FileText, Download, AlertCircle, CheckCircle, XCircle, RefreshCw, Calendar, User, Package, DollarSign, Search, Filter, ChevronUp, ChevronDown, ArrowUpDown, Eye } from 'lucide-react';
import { getSalesDataFileDetail, getSalesDataRecords, getValidationHistory } from '../config/api';

const SalesDataFileModal = ({ fileId, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [recordsPage, setRecordsPage] = useState(1);
  const [recordsLimit] = useState(20);
  const [recordsSearch, setRecordsSearch] = useState('');
  const [recordsSortField, setRecordsSortField] = useState('recordNumber');
  const [recordsSortDirection, setRecordsSortDirection] = useState('asc');
  const [recordsFilter, setRecordsFilter] = useState({
    validationStatus: '',
    customerId: '',
    productId: ''
  });
  const [validationPage, setValidationPage] = useState(1);
  const [validationLimit] = useState(10);
  const [validationFilter, setValidationFilter] = useState({
    validationType: '',
    severity: '',
    status: ''
  });

  const { data: fileData, isLoading, error } = useQuery({
    queryKey: ['salesDataFileDetail', fileId],
    queryFn: () => getSalesDataFileDetail(fileId),
    enabled: isOpen && !!fileId,
    retry: 1
  });

  // Fetch sales data records
  const { data: recordsData, isLoading: recordsLoading } = useQuery({
    queryKey: ['salesDataRecords', fileId, recordsPage, recordsLimit, recordsSearch, recordsSortField, recordsSortDirection, recordsFilter],
    queryFn: () => getSalesDataRecords({
      fileId,
      page: recordsPage,
      limit: recordsLimit,
      search: recordsSearch || undefined,
      sortField: recordsSortField,
      sortDirection: recordsSortDirection,
      ...recordsFilter
    }),
    enabled: isOpen && !!fileId && activeTab === 'records',
    retry: 1
  });

  // Fetch validation results
  const { data: validationData, isLoading: validationLoading } = useQuery({
    queryKey: ['salesDataValidation', fileId, validationPage, validationLimit, validationFilter],
    queryFn: () => getValidationHistory({
      fileId,
      page: validationPage,
      limit: validationLimit,
      ...validationFilter
    }),
    enabled: isOpen && !!fileId && activeTab === 'validation',
    retry: 1
  });

  const file = fileData?.data;

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PROCESSED':
        return 'text-green-600 bg-green-100';
      case 'PROCESSING':
        return 'text-blue-600 bg-blue-100';
      case 'FAILED':
        return 'text-red-600 bg-red-100';
      case 'PARTIALLY_PROCESSED':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PROCESSED':
        return CheckCircle;
      case 'PROCESSING':
        return RefreshCw;
      case 'FAILED':
        return XCircle;
      case 'PARTIALLY_PROCESSED':
        return AlertCircle;
      default:
        return FileText;
    }
  };

  const handleRecordsSort = (field) => {
    if (recordsSortField === field) {
      setRecordsSortDirection(recordsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setRecordsSortField(field);
      setRecordsSortDirection('asc');
    }
    setRecordsPage(1); // Reset to first page when sorting
  };

  const getSortIcon = (field) => {
    if (recordsSortField !== field) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return recordsSortDirection === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-blue-600" />
      : <ChevronDown className="w-4 h-4 text-blue-600" />;
  };

  const handleRecordsFilterChange = (key, value) => {
    setRecordsFilter(prev => ({
      ...prev,
      [key]: value
    }));
    setRecordsPage(1); // Reset to first page when filtering
  };

  const handleRecordsSearch = (value) => {
    setRecordsSearch(value);
    setRecordsPage(1); // Reset to first page when searching
  };

  const getValidationStatusColor = (status) => {
    switch (status) {
      case 'VALID':
        return 'text-green-600 bg-green-100';
      case 'INVALID':
        return 'text-red-600 bg-red-100';
      case 'WARNING':
        return 'text-yellow-600 bg-yellow-100';
      case 'PENDING':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'ERROR':
        return 'text-red-600 bg-red-100';
      case 'WARNING':
        return 'text-yellow-600 bg-yellow-100';
      case 'INFO':
        return 'text-blue-600 bg-blue-100';
      case 'SUCCESS':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getValidationTypeIcon = (type) => {
    switch (type) {
      case 'CUSTOMER_VALIDATION':
        return User;
      case 'PRODUCT_VALIDATION':
        return Package;
      case 'PRICE_VALIDATION':
        return DollarSign;
      case 'CONTRACT_VALIDATION':
        return FileText;
      case 'DATA_FORMAT_VALIDATION':
        return AlertCircle;
      default:
        return CheckCircle;
    }
  };

  const handleValidationFilterChange = (key, value) => {
    setValidationFilter(prev => ({
      ...prev,
      [key]: value
    }));
    setValidationPage(1);
  };

  if (!isOpen) return null;

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading file details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="flex items-center text-red-600">
            <XCircle className="h-6 w-6 mr-3" />
            <div>
              <h3 className="text-lg font-medium">Error Loading File</h3>
              <p className="text-sm text-gray-600 mt-1">
                Failed to load file details. Please try again.
              </p>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              title="Close"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const StatusIcon = getStatusIcon(file?.status);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <FileText className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {file?.originalFileName}
              </h2>
              <p className="text-sm text-gray-500">
                Sales Data File Details
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              title="File Details"
            >
              File Details
            </button>
            <button
              onClick={() => setActiveTab('records')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'records'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              title="Records"
            >
              Records ({file?.totalRecords || 0})
            </button>
            <button
              onClick={() => setActiveTab('validation')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'validation'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              title="Validation Results"
            >
              Validation Results
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-96">
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* File Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <FileText className="h-5 w-5 mr-2" />
                    File Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">File Name:</span>
                      <span className="text-sm font-medium text-gray-900">{file?.originalFileName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">File Size:</span>
                      <span className="text-sm font-medium text-gray-900">{formatFileSize(file?.fileSize)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">File Type:</span>
                      <span className="text-sm font-medium text-gray-900">{file?.fileType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Upload Date:</span>
                      <span className="text-sm font-medium text-gray-900">{formatDate(file?.uploadDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Status:</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(file?.status)}`}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {file?.status?.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <DollarSign className="h-5 w-5 mr-2" />
                    Processing Statistics
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Records:</span>
                      <span className="text-sm font-medium text-gray-900">{file?.totalRecords?.toLocaleString() || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Processed Records:</span>
                      <span className="text-sm font-medium text-gray-900">{file?.processedRecords?.toLocaleString() || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Error Records:</span>
                      <span className="text-sm font-medium text-red-600">{file?.errorRecords || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Duplicate Records:</span>
                      <span className="text-sm font-medium text-yellow-600">{file?.duplicates || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Success Rate:</span>
                      <span className="text-sm font-medium text-green-600">
                        {file?.totalRecords > 0 
                          ? ((file?.processedRecords / file?.totalRecords) * 100).toFixed(1)
                          : 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Processing Details */}
              {file?.processingDetails && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <RefreshCw className="h-5 w-5 mr-2" />
                    Processing Details
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Started At:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {file.processingDetails.startedAt ? formatDate(file.processingDetails.startedAt) : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Completed At:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {file.processingDetails.completedAt ? formatDate(file.processingDetails.completedAt) : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Processing Time:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {file.processingDetails.processingTime || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Details */}
              {file?.errorDetails && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-red-900 mb-4 flex items-center">
                    <XCircle className="h-5 w-5 mr-2" />
                    Error Details
                  </h3>
                  <div className="text-sm text-red-700">
                    <p className="font-medium">Error Message:</p>
                    <p className="mt-1">{file.errorDetails.message}</p>
                    {file.errorDetails.details && (
                      <div className="mt-2">
                        <p className="font-medium">Details:</p>
                        <pre className="mt-1 text-xs bg-red-100 p-2 rounded overflow-x-auto">
                          {JSON.stringify(file.errorDetails.details, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'records' && (
            <div className="space-y-4">
              {/* Records Search and Filters */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Search Records</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={recordsSearch}
                        onChange={(e) => handleRecordsSearch(e.target.value)}
                        placeholder="Search by customer, product, or record number..."
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Validation Status</label>
                    <select
                      value={recordsFilter.validationStatus}
                      onChange={(e) => handleRecordsFilterChange('validationStatus', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">All Status</option>
                      <option value="VALID">Valid</option>
                      <option value="INVALID">Invalid</option>
                      <option value="WARNING">Warning</option>
                      <option value="PENDING">Pending</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                    <select
                      value={recordsSortField}
                      onChange={(e) => setRecordsSortField(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="recordNumber">Record Number</option>
                      <option value="customerName">Customer Name</option>
                      <option value="productName">Product Name</option>
                      <option value="salesDate">Sales Date</option>
                      <option value="quantity">Quantity</option>
                      <option value="unitPrice">Unit Price</option>
                      <option value="totalAmount">Total Amount</option>
                      <option value="validationStatus">Validation Status</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Records Table */}
              {recordsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading records...</span>
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleRecordsSort('recordNumber')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Record #</span>
                              {getSortIcon('recordNumber')}
                            </div>
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleRecordsSort('customerName')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Customer</span>
                              {getSortIcon('customerName')}
                            </div>
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleRecordsSort('productName')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Product</span>
                              {getSortIcon('productName')}
                            </div>
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleRecordsSort('quantity')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Qty</span>
                              {getSortIcon('quantity')}
                            </div>
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleRecordsSort('unitPrice')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Unit Price</span>
                              {getSortIcon('unitPrice')}
                            </div>
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleRecordsSort('totalAmount')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Total</span>
                              {getSortIcon('totalAmount')}
                            </div>
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleRecordsSort('validationStatus')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Status</span>
                              {getSortIcon('validationStatus')}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {recordsData?.data?.records?.map((record) => (
                          <tr key={record.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {record.recordNumber}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>
                                <div className="font-medium">{record.customerName}</div>
                                <div className="text-gray-500">{record.customerId}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>
                                <div className="font-medium">{record.productName}</div>
                                <div className="text-gray-500">{record.productId}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {record.quantity ? Number(record.quantity).toLocaleString() : '0'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              ${record.unitPrice ? Number(record.unitPrice).toFixed(2) : '0.00'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              ${record.totalAmount ? Number(record.totalAmount).toFixed(2) : '0.00'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getValidationStatusColor(record.validationStatus)}`}>
                                {record.validationStatus}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button className="text-blue-600 hover:text-blue-900" title="View record">
                                <Eye className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        )) || []}
                      </tbody>
                    </table>
                  </div>

                  {/* Records Pagination */}
                  {recordsData?.data?.pagination && recordsData.data.pagination.pages > 1 && (
                    <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                          Showing {((recordsData.data.pagination.page - 1) * recordsData.data.pagination.limit) + 1} to {Math.min(recordsData.data.pagination.page * recordsData.data.pagination.limit, recordsData.data.pagination.total)} of {recordsData.data.pagination.total} records
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setRecordsPage(recordsPage - 1)}
                            disabled={recordsPage <= 1}
                            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Previous page"
                          >
                            Previous
                          </button>
                          <span className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-md">
                            {recordsPage}
                          </span>
                          <button
                            onClick={() => setRecordsPage(recordsPage + 1)}
                            disabled={recordsPage >= recordsData.data.pagination.pages}
                            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Next page"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* No Records Message */}
                  {(!recordsData?.data?.records || recordsData.data.records.length === 0) && !recordsLoading && (
                    <div className="text-center py-8">
                      <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Records Found</h3>
                      <p className="text-gray-500">
                        {recordsSearch || Object.values(recordsFilter).some(f => f) 
                          ? 'No records match your search criteria.' 
                          : 'No records available for this file.'}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'validation' && (
            <div className="space-y-6">
              {/* Validation Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-800">Valid Records</p>
                      <p className="text-2xl font-bold text-green-900">
                        {file?.processedRecords || 0}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <XCircle className="h-8 w-8 text-red-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">Error Records</p>
                      <p className="text-2xl font-bold text-red-900">
                        {file?.errorRecords || 0}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertCircle className="h-8 w-8 text-yellow-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-yellow-800">Warning Records</p>
                      <p className="text-2xl font-bold text-yellow-900">
                        {file?.warningRecords || 0}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <RefreshCw className="h-8 w-8 text-blue-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-blue-800">Success Rate</p>
                      <p className="text-2xl font-bold text-blue-900">
                        {file?.totalRecords > 0 
                          ? ((file?.processedRecords / file?.totalRecords) * 100).toFixed(1)
                          : 0}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Validation Filters */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Validation Type</label>
                    <select
                      value={validationFilter.validationType}
                      onChange={(e) => handleValidationFilterChange('validationType', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">All Types</option>
                      <option value="CUSTOMER_VALIDATION">Customer Validation</option>
                      <option value="PRODUCT_VALIDATION">Product Validation</option>
                      <option value="PRICE_VALIDATION">Price Validation</option>
                      <option value="CONTRACT_VALIDATION">Contract Validation</option>
                      <option value="DATA_FORMAT_VALIDATION">Data Format Validation</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                    <select
                      value={validationFilter.severity}
                      onChange={(e) => handleValidationFilterChange('severity', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">All Severities</option>
                      <option value="ERROR">Error</option>
                      <option value="WARNING">Warning</option>
                      <option value="INFO">Info</option>
                      <option value="SUCCESS">Success</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={validationFilter.status}
                      onChange={(e) => handleValidationFilterChange('status', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">All Status</option>
                      <option value="RESOLVED">Resolved</option>
                      <option value="UNRESOLVED">Unresolved</option>
                      <option value="IN_PROGRESS">In Progress</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Validation Results Table */}
              {validationLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading validation results...</span>
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Message
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Severity
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Record
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {validationData?.data?.validations?.map((validation) => {
                          const TypeIcon = getValidationTypeIcon(validation.validationType);
                          return (
                            <tr key={validation.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <TypeIcon className="h-5 w-5 text-gray-400 mr-2" />
                                  <span className="text-sm font-medium text-gray-900">
                                    {validation.validationType?.replace('_', ' ')}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-900 max-w-xs">
                                  <p className="font-medium">{validation.message}</p>
                                  {validation.details && (
                                    <p className="text-gray-500 text-xs mt-1">
                                      {validation.details}
                                    </p>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(validation.severity)}`}>
                                  {validation.severity}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {validation.recordNumber || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getValidationStatusColor(validation.status)}`}>
                                  {validation.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(validation.createdAt)}
                              </td>
                            </tr>
                          );
                        }) || []}
                      </tbody>
                    </table>
                  </div>

                  {/* Validation Pagination */}
                  {validationData?.data?.pagination && validationData.data.pagination.pages > 1 && (
                    <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                          Showing {((validationData.data.pagination.page - 1) * validationData.data.pagination.limit) + 1} to {Math.min(validationData.data.pagination.page * validationData.data.pagination.limit, validationData.data.pagination.total)} of {validationData.data.pagination.total} validations
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setValidationPage(validationPage - 1)}
                            disabled={validationPage <= 1}
                            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Previous page"
                          >
                            Previous
                          </button>
                          <span className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-md">
                            {validationPage}
                          </span>
                          <button
                            onClick={() => setValidationPage(validationPage + 1)}
                            disabled={validationPage >= validationData.data.pagination.pages}
                            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Next page"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* No Validations Message */}
                  {(!validationData?.data?.validations || validationData.data.validations.length === 0) && !validationLoading && (
                    <div className="text-center py-8">
                      <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Validation Results</h3>
                      <p className="text-gray-500">
                        {Object.values(validationFilter).some(f => f) 
                          ? 'No validation results match your filter criteria.' 
                          : 'No validation results available for this file.'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Validation Statistics */}
              {validationData?.data?.statistics && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Validation Statistics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {validationData.data.statistics.totalValid || 0}
                      </p>
                      <p className="text-sm text-gray-600">Valid Validations</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-600">
                        {validationData.data.statistics.totalErrors || 0}
                      </p>
                      <p className="text-sm text-gray-600">Errors</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-yellow-600">
                        {validationData.data.statistics.totalWarnings || 0}
                      </p>
                      <p className="text-sm text-gray-600">Warnings</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        {validationData.data.statistics.resolutionRate || 0}%
                      </p>
                      <p className="text-sm text-gray-600">Resolution Rate</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-500">
            File ID: {file?.id}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              title="Close"
            >
              Close
            </button>
            <button
              onClick={() => {
                // Download functionality
                if (file?.id) {
                  // Implementation would go here
                }
              }}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              title="Download"
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesDataFileModal;
